import { Checkbox, Group, Input, Modal, Stack, Text } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button } from "src/components/shared/Button/Button";
import DateRangeInput from "src/components/shared/_FormInputs/DateRangeInput";
import MultiSelect from "src/components/shared/_FormInputs/MultiSelect";
import Select from "src/components/shared/_FormInputs/Select";
import { useSummaryFilters } from "src/hooks/useSummaryFilters";
import { Flex } from "src/styled";

interface ILogbookOption {
  label?: string;
  value: string;
}

export interface AdvancedFiltersValues {
  logbook?: string[];
  status?: string[];
  dateFrom?: string;
  dateTo?: string;
  ref?: string;
  hasSnapshot: boolean;
  hasAttachments: boolean;
  hasChecklists: boolean;
  sortBy?: string;
  direction: "asc" | "desc";
}

export const buildSummarySearchParams = (
  vals: AdvancedFiltersValues,
  currentSearchParams?: URLSearchParams,
) => {
  const params = new URLSearchParams(currentSearchParams ?? "");

  if (vals.sortBy) {
    params.set("sort", vals.sortBy);
    params.set("dir", vals.direction);
  } else {
    params.delete("sort");
    params.delete("dir");
  }

  if (vals.dateFrom) params.set("dateFrom", vals.dateFrom);
  else params.delete("dateFrom");

  if (vals.dateTo) params.set("dateTo", vals.dateTo);
  else params.delete("dateTo");

  if (vals.ref) params.set("ref", vals.ref);
  else params.delete("ref");

  if (vals.logbook && vals.logbook.length > 0) {
    params.delete("logbook");
    vals.logbook.forEach(lb => params.append("logbook", lb));
  } else {
    params.delete("logbook");
  }

  if (vals.status && vals.status.length > 0) {
    params.delete("status");
    vals.status.forEach(st => params.append("status", st));
  } else {
    params.delete("status");
  }

  if (vals.hasSnapshot) params.set("hasSnapshot", "1");
  else params.delete("hasSnapshot");

  if (vals.hasAttachments) params.set("hasAttachments", "1");
  else params.delete("hasAttachments");

  if (vals.hasChecklists) params.set("hasChecklists", "1");
  else params.delete("hasChecklists");

  params.set("page", "1");

  return params;
};

export const buildSummaryRequestBody = (
  vals: AdvancedFiltersValues,
  entriesType: string,
) => ({
  params: {
    req: entriesType,
    ...(vals.sortBy
      ? {
          sort: vals.sortBy,
          dir: vals.direction,
        }
      : {}),
    ...(vals.dateFrom && {
      dateFrom: vals.dateFrom,
    }),
    ...(vals.dateTo && {
      dateTo: vals.dateTo,
    }),
    ...(vals.hasSnapshot && { hasSnapshot: 1 }),
    ...(vals.hasAttachments && { hasAttachments: 1 }),
    ...(vals.hasChecklists && { hasChecklists: 1 }),
    ...(vals.ref && { ref: vals.ref }),
  },
  ...(vals.logbook &&
    vals.logbook.length > 0 && {
      _logbook: vals.logbook.length === 1 ? vals.logbook[0] : vals.logbook,
    }),
  ...(vals.status &&
    vals.status.length > 0 && {
      _status:
        vals.status.length === 1
          ? Number(vals.status[0])
          : vals.status.map(Number),
    }),
  _page: 1,
});

interface Props {
  opened: boolean;
  onClose: () => void;
  logbooks?: ILogbookOption[];
  replaceMaritimeText?: (t: string | undefined) => string | undefined;
  searchParams?: URLSearchParams;
  onGeneratePdf?: (vals: AdvancedFiltersValues) => void | Promise<void>;
  pdfLoading?: boolean;
}

const AdvancedFiltersModal = ({
  opened,
  onClose,
  logbooks = [],
  replaceMaritimeText = t => t,
  searchParams,
  onGeneratePdf,
  pdfLoading = false,
}: Props) => {
  const navigate = useNavigate();
  const filters = useSummaryFilters();
  const [submitting, setSubmitting] = useState(false);
  const { control, getValues, setValue, watch } = useForm({
    defaultValues: {
      logbook: undefined as string[] | undefined,
      status: undefined as string[] | undefined,
      dateFrom: undefined as string | undefined,
      dateTo: undefined as string | undefined,
      ref: undefined as string | undefined,
      hasSnapshot: false,
      hasAttachments: false,
      hasChecklists: false,
      sortBy: undefined as string | undefined,
      direction: "asc" as "asc" | "desc",
    },
  });

  // Populate form with current filter state when modal opens or filters change
  useEffect(() => {
    if (opened) {
      setValue(
        "logbook",
        filters.logbook.length > 0 ? filters.logbook : undefined,
      );
      setValue(
        "status",
        filters.status.length > 0
          ? filters.status.map(s => String(s))
          : undefined,
      );
      setValue("dateFrom", filters.dateFrom);
      setValue("dateTo", filters.dateTo);
      setValue("ref", filters.ref);
      setValue("hasSnapshot", filters.hasSnapshot);
      setValue("hasAttachments", filters.hasAttachments);
      setValue("hasChecklists", filters.hasChecklists);
      setValue("sortBy", filters.sort);
      setValue("direction", (filters.dir as "asc" | "desc") ?? "asc");
    }
  }, [opened, filters, setValue]);

  const sortBy = watch("sortBy");
  const lbvalues = watch("logbook");
  const dateFrom = watch("dateFrom");
  const dateTo = watch("dateTo");

  const status = [
    { label: "Open", value: "1" },
    { label: replaceMaritimeText?.("Yacht Verified"), value: "3" },
    { label: "Withdrawn", value: "99" },
  ];

  const sortByOptions = [
    { label: "Entry Date", value: "date" },
    { label: "Open Date", value: "open" },
  ];

  const directionOptions = [
    { label: "Ascending", value: "asc" },
    { label: "Descending", value: "desc" },
  ];

  const handleClear = () => {
    setValue("logbook", []);
    setValue("status", []);
    setValue("dateFrom", undefined);
    setValue("dateTo", undefined);
    setValue("ref", undefined);
    setValue("hasSnapshot", false);
    setValue("hasAttachments", false);
    setValue("hasChecklists", false);
    setValue("sortBy", undefined);
    setValue("direction", "asc");
    const params = new URLSearchParams(searchParams ?? "");
    [
      "dateFrom",
      "dateTo",
      "ref",
      "logbook",
      "status",
      "hasSnapshot",
      "hasAttachments",
      "hasChecklists",
      "sort",
      "dir",
    ].forEach(k => params.delete(k));
    params.set("page", "1");
    navigate(`?${params.toString()}`, { replace: true });
  };

  const handleApply = () => {
    setSubmitting(true);
    const vals = getValues();
    if (!vals) return;
    if (
      vals.logbook?.length === 0 ||
      (vals.logbook?.length === 1 && vals.logbook[0] === "*")
    )
      return;

    const params = buildSummarySearchParams(vals, searchParams);

    navigate(`?${params.toString()}`, { replace: true });
    onClose();
  };

  const shouldDisablePDF = useMemo(() => {
    if (!lbvalues) return true;
    if (lbvalues.length === 0 || (lbvalues.length === 1 && lbvalues[0] === "*"))
      return true;
    return false;
  }, [lbvalues]);

  const handleGeneratePdf = () => {
    if (shouldDisablePDF) return;
    const vals = getValues();
    void onGeneratePdf?.(vals);
  };

  return (
    <Modal opened={opened} onClose={onClose} size="lg" title="Advanced Filters">
      <Stack gap="sm">
        <Text fw={600}>Date range</Text>
        <DateRangeInput
          names={["dateFrom", "dateTo"]}
          values={[dateFrom, dateTo]}
          setValue={setValue}
          returnFormat="YYYY-MM-DD"
          clearable
        />

        <Text fw={600}>Entry Ref. #</Text>
        <Controller
          name="ref"
          control={control}
          render={({ field }) => <Input {...field} value={field.value ?? ""} />}
        />

        <Text fw={600}>Logbook</Text>
        <MultiSelect
          name="logbook"
          control={control}
          data={logbooks}
          placeholder="Select logbook"
          searchable
          clearable
          onChange={() => setSubmitting(false)}
          error={
            submitting &&
            (!lbvalues ||
              lbvalues.length === 0 ||
              (lbvalues.length === 1 && lbvalues[0] === "*"))
          }
        />

        <Text fw={600}>Status</Text>
        <MultiSelect
          name="status"
          control={control}
          data={status}
          placeholder="Select status"
          clearable
        />
        <Flex gap="sm">
          <Flex direction="column" gap="sm" flex={1}>
            <Text fw={600}>Sort by</Text>
            <Select
              name="sortBy"
              control={control}
              data={sortByOptions}
              placeholder="Select sort field"
              clearable
            />
          </Flex>

          {sortBy && sortByOptions.some(s => s.value === sortBy) && (
            <Flex direction="column" gap="sm" flex={1}>
              <Text fw={600}>Sort direction</Text>
              <Select
                name="direction"
                control={control}
                data={directionOptions}
                placeholder="Select direction"
              />
            </Flex>
          )}
        </Flex>

        <Text fw={600}>Other filters</Text>
        <Flex direction="column" gap={12}>
          <Controller
            name="hasSnapshot"
            control={control}
            render={({ field }) => (
              <Checkbox
                label="Has historical snapshot"
                checked={field.value}
                onChange={e => field.onChange(e.currentTarget.checked)}
              />
            )}
          />
          <Controller
            name="hasAttachments"
            control={control}
            render={({ field }) => (
              <Checkbox
                label="Has attachments"
                checked={field.value}
                onChange={e => field.onChange(e.currentTarget.checked)}
              />
            )}
          />
          <Controller
            name="hasChecklists"
            control={control}
            render={({ field }) => (
              <Checkbox
                label="Has checklists"
                checked={field.value}
                onChange={e => field.onChange(e.currentTarget.checked)}
              />
            )}
          />
        </Flex>

        <Group justify="apart" mt="md">
          <Button color="red" onClick={onClose}>
            Cancel
          </Button>
          <Group>
            <Button
              variant="outline"
              color="blue"
              onClick={handleGeneratePdf}
              loading={pdfLoading}
              disabled={!onGeneratePdf || shouldDisablePDF}>
              Generate PDF
            </Button>
            <Button variant="outline" color="gray" onClick={handleClear}>
              Clear Filters
            </Button>
            <Button onClick={handleApply}>Apply</Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};

export default AdvancedFiltersModal;
